import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { COMPANY } from "@/lib/index";

const springGentle = { type: "spring" as const, stiffness: 300, damping: 35 };

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mb-10">
    <h2 className="font-heading text-xl font-bold text-foreground mb-4 pb-2 border-b border-border">
      {title}
    </h2>
    <div className="text-muted-foreground text-sm leading-relaxed space-y-3">{children}</div>
  </div>
);

export default function Privacy() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-border bg-card/80 sticky top-0 z-40 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            Zurück zur Startseite
          </button>
        </div>
      </div>

      {/* Hero */}
      <div
        className="py-16 border-b border-border relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, oklch(0.10 0.025 195) 0%, oklch(0.13 0.03 185) 100%)" }}
      >
        <div
          className="absolute inset-0 opacity-5"
          style={{ background: "radial-gradient(ellipse at top right, oklch(0.72 0.20 55) 0%, transparent 60%)" }}
        />
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springGentle}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: "oklch(0.72 0.20 55 / 0.15)", color: "oklch(0.72 0.20 55)" }}
              >
                <Shield size={20} />
              </div>
              <span
                className="text-xs font-semibold uppercase tracking-[0.2em] px-3 py-1 rounded-full border"
                style={{ color: "oklch(0.72 0.20 55)", borderColor: "oklch(0.72 0.20 55 / 0.3)", background: "oklch(0.72 0.20 55 / 0.08)" }}
              >
                Rechtliches
              </span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-3">
              Datenschutzerklärung
            </h1>
            <p className="text-muted-foreground text-base">
              Gemäß DSGVO (EU) 2016/679 · Stand: Mai 2026
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...springGentle, delay: 0.15 }}
        className="container mx-auto px-6 py-14 max-w-4xl"
      >
        <Section title="1. Verantwortlicher">
          <p>
            Verantwortlicher im Sinne der Datenschutz-Grundverordnung (DSGVO) und anderer nationaler
            Datenschutzgesetze sowie sonstiger datenschutzrechtlicher Bestimmungen ist:
          </p>
          <div
            className="rounded-xl border border-border p-5 bg-card mt-3 space-y-1"
          >
            <p className="text-foreground font-semibold">{COMPANY.name}</p>
            <p>Inhaber: {COMPANY.founder}</p>
            <p>{COMPANY.address}</p>
            <p>
              Telefon:{" "}
              <a href={`tel:${COMPANY.phone}`} className="hover:text-primary transition-colors">
                {COMPANY.phone}
              </a>
            </p>
            <p>
              E-Mail:{" "}
              <a href={`mailto:${COMPANY.email}`} className="hover:text-primary transition-colors">
                {COMPANY.email}
              </a>
            </p>
            <p>
              Website:{" "}
              <a href={`https://${COMPANY.website}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                {COMPANY.website}
              </a>
            </p>
          </div>
        </Section>

        <Section title="2. Allgemeines zur Datenverarbeitung">
          <p>
            Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre
            personenbezogenen Daten vertraulich und entsprechend der gesetzlichen
            Datenschutzvorschriften sowie dieser Datenschutzerklärung.
          </p>
          <p>
            Die Nutzung unserer Website ist in der Regel ohne Angabe personenbezogener Daten möglich.
            Soweit auf unseren Seiten personenbezogene Daten (beispielsweise Name, Anschrift oder
            E-Mail-Adressen) erhoben werden, erfolgt dies, soweit möglich, stets auf freiwilliger Basis.
            Diese Daten werden ohne Ihre ausdrückliche Zustimmung nicht an Dritte weitergegeben.
          </p>
        </Section>

        <Section title="3. Rechtsgrundlagen der Verarbeitung">
          <p>
            Die Verarbeitung personenbezogener Daten erfolgt auf Basis folgender Rechtsgrundlagen:
          </p>
          <ul className="list-none space-y-2 mt-2">
            {[
              { art: "Art. 6 Abs. 1 lit. a DSGVO", desc: "Einwilligung der betroffenen Person" },
              { art: "Art. 6 Abs. 1 lit. b DSGVO", desc: "Erfüllung eines Vertrags oder vorvertraglicher Maßnahmen" },
              { art: "Art. 6 Abs. 1 lit. c DSGVO", desc: "Erfüllung einer rechtlichen Verpflichtung" },
              { art: "Art. 6 Abs. 1 lit. f DSGVO", desc: "Wahrung berechtigter Interessen" },
            ].map((item) => (
              <li key={item.art} className="flex items-start gap-2">
                <span
                  className="inline-block mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: "oklch(0.72 0.20 55)", marginTop: "6px" }}
                />
                <span>
                  <strong className="text-foreground/80">{item.art}:</strong> {item.desc}
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="4. Erhebung und Speicherung personenbezogener Daten">
          <p>
            <strong className="text-foreground/80">Server-Log-Dateien:</strong> Der Provider unserer
            Seiten erhebt und speichert automatisch Informationen in sogenannten Server-Log-Dateien,
            die Ihr Browser automatisch übermittelt. Dies sind:
          </p>
          <ul className="list-none space-y-1 pl-4 mt-2">
            {[
              "Browsertyp und Browserversion",
              "verwendetes Betriebssystem",
              "Referrer URL",
              "Hostname des zugreifenden Rechners",
              "Uhrzeit der Serveranfrage",
              "IP-Adresse",
            ].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "oklch(0.72 0.20 55)" }} />
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-3">
            Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen. Die
            Verarbeitung erfolgt gemäß Art. 6 Abs. 1 lit. f DSGVO auf Basis unseres berechtigten
            Interesses an der technischen Bereitstellung der Website.
          </p>
        </Section>

        <Section title="5. Kontaktformular und E-Mail-Kontakt">
          <p>
            Wenn Sie uns per Kontaktformular oder E-Mail Anfragen zukommen lassen, werden Ihre Angaben
            aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks
            Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert.
          </p>
          <p>
            Die Verarbeitung der in das Kontaktformular eingegebenen Daten erfolgt ausschließlich auf
            Grundlage Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). Sie können diese Einwilligung
            jederzeit widerrufen. Dazu reicht eine formlose Mitteilung per E-Mail an uns.
          </p>
          <p>
            Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei uns, bis Sie uns zur
            Löschung auffordern, Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für die
            Datenspeicherung entfällt (z. B. nach abgeschlossener Bearbeitung Ihrer Anfrage).
            Zwingende gesetzliche Bestimmungen – insbesondere Aufbewahrungsfristen – bleiben unberührt.
          </p>
        </Section>

        <Section title="6. Cookies">
          <p>
            Unsere Website verwendet Cookies. Das sind kleine Textdateien, die Ihr Webbrowser auf
            Ihrem Endgerät speichert. Cookies helfen uns dabei, unser Angebot nutzerfreundlicher,
            effektiver und sicherer zu machen.
          </p>
          <p>
            Einige Cookies sind „Session-Cookies". Solche Cookies werden nach Ende Ihrer
            Browser-Sitzung von selbst gelöscht. Hingegen bleiben andere Cookies auf Ihrem Endgerät
            bestehen, bis Sie diese selbst löschen. Solche Cookies helfen uns, Sie beim nächsten
            Besuch wiederzuerkennen.
          </p>
          <p>
            Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert
            werden und Cookies nur im Einzelfall erlauben, die Annahme von Cookies für bestimmte Fälle
            oder generell ausschließen sowie das automatische Löschen der Cookies beim Schließen des
            Browsers aktivieren.
          </p>
        </Section>

        <Section title="7. Ihre Rechte als betroffene Person">
          <p>Sie haben gegenüber uns folgende Rechte hinsichtlich der Sie betreffenden personenbezogenen Daten:</p>
          <ul className="list-none space-y-2 mt-2">
            {[
              { recht: "Recht auf Auskunft", desc: "Sie können Auskunft über Ihre von uns verarbeiteten personenbezogenen Daten verlangen (Art. 15 DSGVO)." },
              { recht: "Recht auf Berichtigung", desc: "Sie können unverzüglich die Berichtigung unrichtiger oder Vervollständigung Ihrer bei uns gespeicherten Daten verlangen (Art. 16 DSGVO)." },
              { recht: "Recht auf Löschung", desc: "Sie können die Löschung Ihrer bei uns gespeicherten personenbezogenen Daten verlangen (Art. 17 DSGVO)." },
              { recht: "Recht auf Einschränkung", desc: "Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen (Art. 18 DSGVO)." },
              { recht: "Recht auf Datenübertragbarkeit", desc: "Sie haben das Recht, Ihre Daten in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten (Art. 20 DSGVO)." },
              { recht: "Widerspruchsrecht", desc: "Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben, jederzeit Widerspruch einzulegen (Art. 21 DSGVO)." },
              { recht: "Beschwerderecht", desc: "Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über die Verarbeitung Ihrer personenbezogenen Daten zu beschweren." },
            ].map((item) => (
              <li key={item.recht} className="flex items-start gap-2">
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: "oklch(0.72 0.20 55)", marginTop: "6px" }}
                />
                <span>
                  <strong className="text-foreground/80">{item.recht}:</strong> {item.desc}
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="8. SSL-/TLS-Verschlüsselung">
          <p>
            Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher
            Inhalte eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie
            daran, dass die Adresszeile des Browsers von „http://" auf „https://" wechselt und an dem
            Schloss-Symbol in Ihrer Browserzeile.
          </p>
          <p>
            Wenn die SSL- bzw. TLS-Verschlüsselung aktiviert ist, können die Daten, die Sie an uns
            übermitteln, nicht von Dritten mitgelesen werden.
          </p>
        </Section>

        <Section title="9. Externe Links und Social Media">
          <p>
            Unsere Website enthält Links zu externen Websites, insbesondere zu{" "}
            <strong className="text-foreground/80">Facebook</strong> (Meta Platforms Ireland Limited,
            4 Grand Canal Square, Grand Canal Harbour, Dublin 2, Irland). Wenn Sie auf diese Links
            klicken, verlassen Sie unsere Website. Für den Datenschutz auf diesen Seiten sind wir
            nicht verantwortlich. Wir empfehlen, die Datenschutzerklärungen der jeweiligen Anbieter
            zu lesen.
          </p>
          <p>
            Meta (Facebook) Datenschutzerklärung:{" "}
            <a
              href="https://www.facebook.com/privacy/policy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors underline underline-offset-2"
            >
              facebook.com/privacy/policy
            </a>
          </p>
        </Section>

        <Section title="10. Änderungen dieser Datenschutzerklärung">
          <p>
            Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen
            rechtlichen Anforderungen entspricht oder um Änderungen unserer Leistungen in der
            Datenschutzerklärung umzusetzen, z. B. bei der Einführung neuer Services. Für Ihren
            erneuten Besuch gilt dann die neue Datenschutzerklärung.
          </p>
        </Section>

        <Section title="11. Kontakt zum Datenschutz">
          <p>
            Bei Fragen zum Datenschutz schreiben Sie uns bitte eine E-Mail oder wenden Sie sich
            direkt an uns:
          </p>
          <div className="rounded-xl border border-border p-5 bg-card mt-3 flex flex-col gap-2 text-sm">
            <p className="text-foreground font-semibold">{COMPANY.name} – Datenschutz</p>
            <p>{COMPANY.address}</p>
            <a href={`mailto:${COMPANY.email}`} className="hover:text-primary transition-colors">
              {COMPANY.email}
            </a>
            <a href={`tel:${COMPANY.phone}`} className="hover:text-primary transition-colors">
              {COMPANY.phone}
            </a>
          </div>
        </Section>

        {/* Back button */}
        <div className="pt-8 border-t border-border">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold text-sm hover:bg-accent transition-all hover:scale-105"
          >
            <ArrowLeft size={16} />
            Zurück zur Startseite
          </button>
        </div>
      </motion.div>
    </div>
  );
}
